/*
 * Delete in one click, with a window to take it back.
 *
 * The old flow was: click delete, read a modal, confirm, wait, dismiss. This
 * removes the row immediately and shows an Undo bar for a few seconds. The
 * DELETE request is only sent once that window closes, so pressing Undo costs
 * nothing and nothing is destroyed early -- it is safer than the confirm it
 * replaces, not just quicker.
 *
 * A component opts in by adding the mixin and rendering <undo-bar> once:
 *
 *   import undoable from "@/mixins/undoable";
 *   export default { mixins: [undoable], ... }
 *
 *   this.undoableRemove({
 *     item,
 *     list: this.buses,
 *     label: bus.license,
 *     request: () => axios.delete(`/buses/${bus.id}`),
 *     onDone:  () => this.$notifySuccess(this.$t("bus_deleted")),
 *     onError: (error) => this.$notifyError(error, this.$t("error_deleting_bus")),
 *   });
 */

const UNDO_WINDOW_MS = 5000;

export default {
  data() {
    return {
      pendingUndo: null, // { label, timer, restore, run }
      undoSecondsLeft: 0,
      undoTicker: null,
    };
  },

  beforeDestroy() {
    //leaving the screen commits whatever is still pending, so a delete the
    //admin asked for is never silently dropped
    this.flushPendingUndo();
  },

  methods: {
    /**
     * Remove an item optimistically and schedule the request.
     *
     * @param {object}   options.item     the row being removed
     * @param {Array}    options.list     the array it lives in
     * @param {string}   options.label    shown in the undo bar
     * @param {Function} options.request  returns the axios promise; called after the window
     * @param {Function} [options.onDone]
     * @param {Function} [options.onError]
     */
    undoableRemove({ item, list, label, request, onDone, onError }) {
      //only one pending removal at a time: commit the previous one first
      this.flushPendingUndo();

      const index = list.indexOf(item);
      if (index === -1) {
        return;
      }

      list.splice(index, 1);

      const restore = () => {
        //put it back exactly where it was
        list.splice(Math.min(index, list.length), 0, item);
      };

      const run = () => {
        Promise.resolve(request())
          .then(() => {
            if (onDone) onDone();
          })
          .catch((error) => {
            //the server refused, so the row belongs back on screen
            restore();
            if (onError) onError(error);
            else this.$notifyError(error);
          });
      };

      const timer = window.setTimeout(() => {
        this.pendingUndo = null;
        this.stopUndoTicker();
        run();
      }, UNDO_WINDOW_MS);

      this.pendingUndo = { label, timer, restore, run };
      this.startUndoTicker();
    },

    /** Take back the pending removal. */
    undoPendingRemove() {
      if (!this.pendingUndo) return;

      window.clearTimeout(this.pendingUndo.timer);
      this.pendingUndo.restore();
      this.pendingUndo = null;
      this.stopUndoTicker();
    },

    /** Send the pending request now instead of waiting out the window. */
    flushPendingUndo() {
      if (!this.pendingUndo) return;

      window.clearTimeout(this.pendingUndo.timer);
      const { run } = this.pendingUndo;
      this.pendingUndo = null;
      this.stopUndoTicker();
      run();
    },

    startUndoTicker() {
      this.undoSecondsLeft = Math.ceil(UNDO_WINDOW_MS / 1000);
      this.stopUndoTicker();
      this.undoTicker = window.setInterval(() => {
        this.undoSecondsLeft -= 1;
        if (this.undoSecondsLeft <= 0) {
          this.stopUndoTicker();
        }
      }, 1000);
    },

    stopUndoTicker() {
      if (this.undoTicker !== null) {
        window.clearInterval(this.undoTicker);
        this.undoTicker = null;
      }
    },
  },
};
